import React, { useCallback, useContext } from 'react';
import { DocumentCarouselContext, IActionCriteria, IDocumentPrint } from '../../components/document/DocumentProvider';
import { useQuery } from '@apollo/client';
import {
  IPaymentPrintMutationResult,
  IPaymentPrintMutationVariables,
  IPaymentReceipt,
  IPaymentReprintMutationResult,
  IPaymentReprintMutationVariables,
  ISalesOrder,
  ISalesOrderDocumentCarouselQueryResult,
  ISalesOrderDocumentCarouselQueryVariables,
  ReportType,
} from '../../graphql/schema/p4types';
import {
  getDocumentCarouselReportParameters,
  IDocumentValues,
  useActionFilters,
  useDocumentCarouselDefaults,
  useSalesOrderDeliveryRecipient,
} from '../../components/document/documentCarouselUtil';
import DocumentCarouselHeader from '../../components/document/DocumentCarouselHeader';
import { FormProvider, useForm } from 'react-hook-form';
import DocumentCarouselTemplate from '../../components/document/DocumentCarouselTemplate';
import DocumentCarouselOutput from '../../components/document/DocumentCarouselOutput';
import DocumentCarouselButtons from '../../components/document/DocumentCarouselButtons';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useDocumentCarouselComplete, useDocumentJobCompletion } from '../../utils/DocumentUtils';
import getClientMutationId from '../../utils/getClientMutationId';
import DocumentCarouselEmail from '../../components/document/DocumentCarouselEmail';
import SalesOrderDocumentCarouselQuery from '../../graphql/queries/document/SalesOrderDocumentCarouselQuery.graphql';
import DocumentCarouselPrintOptions from '../../components/document/DocumentCarouselPrintOptions';
import DocumentCarouselSummary from '../../components/document/DocumentCarouselSummary';
import { useCookies } from 'react-cookie';
import { DocumentCarouselHeaderInfo } from '../../components/document/DocumentCarouselHeaderInfo';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import DocumentCarouselSkeleton from '../../components/document/DocumentCarouselSkeleton';
import { useSalesOrderConfig } from '../../utils/FetchConfig';
import Payment from '../../graphql/entities/sales/Payment';
import { useP4ToastController } from '../system/NotificationsArea';

//Note - DocumentModal2 currently uses 'documentSummary' instead of 'DOCUMENT_SUMMARY' - Possible cause for minor friction in transition.
export const DOCUMENT_SUMMARY = 'DOCUMENT_SUMMARY';
export const PRINT_INDIVIDUAL_PREFERENCE_COOKIE = 'PRINT_INDIVIDUAL_PREFERENCE_COOKIE';

/**
 * Render the content for the Payment Receipt Document Carousel.
 * All querying should be completed before rendering this element so that 'documentDefaults' are accurate/up-to-date when the user see's them.
 * @param documentDefaults - Default document values. Should be final on initial load.
 * @param actionCriteria - The action criteria driving this particular print.
 * @param count - How many records are in this print job.
 * @param singleRecipient - Flag to indicate if all records are a single Customer/Supplier.
 * @param singlePrint - Flag to indicate if we're printing a single record.
 * @param singlePrintNode - If we're printing a single record, this should be the document-queried subset of that record for display/defaulting.
 *                        - Note - Will still be populated for non-single, will be arbitrarily the first record matching the criteria.
 * @param setCookie - Pass-through of cookie setter, used to update 'showSummary' and 'printContiguous' cookie values.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
const PaymentReceiptDocumentContent = ({
  documentDefaults,
  actionCriteria,
  count,
  singleRecipient,
  singlePrint,
  singlePrintNode,
  setCookie,
  documentPrint,
}: {
  documentDefaults: IDocumentValues;
  actionCriteria: IActionCriteria;
  count: number;
  singleRecipient: boolean;
  singlePrint: boolean;
  singlePrintNode: ISalesOrder | IPaymentReceipt;
  setCookie: ReturnType<typeof useCookies>[1];
  documentPrint: IDocumentPrint;
}) => {
  const { onSkip } = useContext(DocumentCarouselContext);
  const { reprint, onPrintComplete } = documentPrint;
  const { dispatchSimpleToast } = useP4ToastController();

  //Use react-hook-form to manage our form values for better typing and performance than ConnectedForm.
  const methods = useForm<IDocumentValues>({
    defaultValues: documentDefaults,
  });
  const { getValues, watch } = methods;

  const onDocumentComplete = useDocumentCarouselComplete({
    watch,
    entity: Payment,
  });

  const onJobStart = useCallback(() => {
    onSkip(true);
  }, [onSkip]);

  const onJobComplete = useDocumentJobCompletion({
    evictionFieldNames: ['salesOrder'],
    onDocumentComplete,
    onPrintComplete,
  });

  const [printPaymentSingle] = useBackgroundMutation<IPaymentPrintMutationResult, IPaymentPrintMutationVariables>(
    Payment.print,
    {
      onStartJob: onJobStart,
      onCompleteJob: onJobComplete,
    }
  );

  const [reprintPaymentSingle] = useBackgroundMutation<IPaymentReprintMutationResult, IPaymentReprintMutationVariables>(
    Payment.reprint!,
    {
      onStartJob: onJobStart,
      onCompleteJob: onJobComplete,
    }
  );

  const onPrintClick = useCallback(() => {
    const reportParameters = getDocumentCarouselReportParameters({ getValues, reprint });

    if (
      !actionCriteria.include ||
      actionCriteria.actionEntities?.length === 0 ||
      actionCriteria.actionEntities?.length > 1
    ) {
      dispatchSimpleToast('Only single-document printing is supported for payments at this time.');
      return;
    }

    // Endpoints take identical parameters, they just filter differently.
    const runMutation = reprint ? reprintPaymentSingle : printPaymentSingle;

    runMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          salesOrder: actionCriteria.actionEntities[0],
          reportParameters: reportParameters,
        },
      },
    });
  }, [
    actionCriteria.actionEntities,
    actionCriteria.include,
    dispatchSimpleToast,
    getValues,
    printPaymentSingle,
    reprint,
    reprintPaymentSingle,
  ]);

  const additionalRecipient = useSalesOrderDeliveryRecipient(singlePrintNode);

  return (
    <FormProvider {...methods}>
      <DocumentCarouselHeader
        entity={Payment}
        count={count}
        secondaryTitle={
          <DocumentCarouselHeaderInfo
            contentLeft={singlePrint ? `Sales Order ${singlePrintNode?.orderNumber}` : undefined}
            contentRight={singleRecipient ? `Customer ${singlePrintNode?.customer?.name ?? 'Unknown'}` : undefined}
          />
        }
        reprint={reprint}
      />
      <div style={{ padding: '1em' }}>
        <DocumentCarouselTemplate reportType={ReportType.PaymentReceipt} singleRecipient={singleRecipient} />
        <DocumentCarouselOutput singleRecipient={singleRecipient} />
        <DocumentCarouselSummary singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselPrintOptions singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselEmail
          entity={Payment}
          recipient={documentDefaults?.recipientForEmail ?? singlePrintNode?.customer}
          singleRecipient={singleRecipient}
          emailOptions={{ additionalRecipients: additionalRecipient ? [additionalRecipient] : [] }}
        />
      </div>
      <DocumentCarouselButtons onPrint={onPrintClick} />
    </FormProvider>
  );
};

/**
 * @param actionCriteria - The action criteria driving this particular print.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
export const PaymentReceiptDocumentCarouselCard = ({
  actionCriteria,
  documentPrint,
}: {
  actionCriteria: IActionCriteria;
  documentPrint: IDocumentPrint;
}) => {
  const { reprint } = documentPrint;

  const filters = useActionFilters(actionCriteria);
  const { data, loading } = useQuery<ISalesOrderDocumentCarouselQueryResult, ISalesOrderDocumentCarouselQueryVariables>(
    SalesOrderDocumentCarouselQuery,
    {
      variables: {
        filters: filters,
        first: 2,
        aggregateCriteria: {
          groupColumns: ['customer.id'],
          sumColumns: ['id'],
        },
        includeCustomer: true,
        includeCustomerCustomerPaymentReceiptDefault: true,
        skip: reprint,
      },
    }
  );

  const singlePrint: boolean = actionCriteria.include && actionCriteria.actionEntities.length === 1;
  //If the grouping gives a single Customer in the result, we can pull the document defaults off the Customer record and use it for the whole print.
  //(If there's >1 Customer, we cannot do this).
  const singleRecipient = data?.listData?.aggregateRows?.length === 1;
  //Note - Take care to always check 'singleRecipient' before consuming this node - singleRecipient *must* be true if consuming this node to avoid bugs.
  //       (This is because for multiple recipients, each node likely has different defaults and we'd only load the 'first' nodes defaults if we use this, potentially overriding the other values with them).
  const singlePrintNode = data?.listData?.edges?.[0]?.node as Maybe<ISalesOrder>;

  const customerPaymentReceiptDefault = singlePrintNode?.customer?.customerPaymentReceiptDefault;
  const paymentReceiptDefault = customerPaymentReceiptDefault?.paymentReceiptDefault;

  const [cookies, setCookie] = useCookies([DOCUMENT_SUMMARY, PRINT_INDIVIDUAL_PREFERENCE_COOKIE]);
  const salesOrderConfig = useSalesOrderConfig();
  const documentDefaults = useDocumentCarouselDefaults({
    entity: Payment,
    recipientDocumentDefault: customerPaymentReceiptDefault,
    commonDocumentDefault: paymentReceiptDefault,
    singleRecipient,
    recipient: singlePrintNode?.customer,
    cookies,
    salesOrderConfig,
  });

  if (data && !loading) {
    return (
      <PaymentReceiptDocumentContent
        documentDefaults={documentDefaults}
        setCookie={setCookie}
        actionCriteria={actionCriteria}
        count={data?.listData?.count ?? 0}
        singleRecipient={singleRecipient}
        singlePrint={singlePrint}
        singlePrintNode={singlePrintNode}
        documentPrint={documentPrint}
      />
    );
  }

  return <DocumentCarouselSkeleton />;
};
